/**
 * TimelineChart Component
 *
 * Horizontal timeline visualization for locations/events over time.
 * Uses static SVG for crisp rendering.
 */

import './charts.css'

/**
 * Convert 2-letter country code to flag emoji
 * Uses Unicode regional indicator symbols
 */
function countryCodeToFlag(code: string): string {
  if (!code || code.length !== 2) return code
  const codePoints = code
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

interface TimelineEntry {
  name: string
  start: number
  end: number
  color: string
  icon?: string
  split?: Array<{
    name: string
    percent: number
    years: number[]
    color: string
  }>
}

interface TimelineChartProps {
  entries: TimelineEntry[]
  startYear: number
  endYear: number
  title?: string
  height?: number
}

export function TimelineChart({
  entries,
  startYear,
  endYear,
  title,
}: TimelineChartProps) {
  const totalYears = endYear - startYear
  const barHeight = 24
  const barGap = 8
  const labelWidth = 140
  const chartWidth = 500 // Will be responsive via CSS
  const padding = { top: 40, right: 20, bottom: 30, left: labelWidth }

  // Calculate total height based on entries
  const chartHeight = entries.length * (barHeight + barGap) + padding.top + padding.bottom

  // Scale function: year to x position
  const yearToX = (year: number) => {
    const ratio = (year - startYear) / totalYears
    return padding.left + ratio * (chartWidth - padding.left - padding.right)
  }

  // Generate year ticks
  const yearTicks: number[] = []
  const tickInterval = totalYears > 30 ? 10 : totalYears > 15 ? 5 : 2
  for (let year = Math.ceil(startYear / tickInterval) * tickInterval; year <= endYear; year += tickInterval) {
    yearTicks.push(year)
  }

  return (
    <div className="timeline-chart">
      {title && <h3 className="chart-title">{title}</h3>}

      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="timeline-chart__svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Year grid lines and labels */}
        <g className="timeline-chart__grid">
          {yearTicks.map((year) => (
            <g key={year}>
              <line
                x1={yearToX(year)}
                y1={padding.top - 10}
                x2={yearToX(year)}
                y2={chartHeight - padding.bottom + 10}
                stroke="#e0e0e0"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text
                x={yearToX(year)}
                y={chartHeight - 10}
                textAnchor="middle"
                className="timeline-chart__tick-label"
              >
                {year}
              </text>
            </g>
          ))}
        </g>

        {/* Timeline bars */}
        <g className="timeline-chart__bars">
          {entries.map((entry, index) => {
            const y = padding.top + index * (barHeight + barGap)
            const x1 = yearToX(entry.start)
            const x2 = yearToX(entry.end)
            const width = x2 - x1

            return (
              <g key={`${entry.name}-${entry.start}`}>
                {/* Label */}
                <text
                  x={padding.left - 8}
                  y={y + barHeight / 2 + 4}
                  textAnchor="end"
                  className="timeline-chart__label"
                >
                  {entry.icon && <tspan>{countryCodeToFlag(entry.icon)} </tspan>}
                  {entry.name}
                </text>

                {/* Main bar */}
                <rect
                  x={x1}
                  y={y}
                  width={width}
                  height={barHeight}
                  rx={4}
                  fill={entry.color}
                  className="timeline-chart__bar"
                >
                  <title>
                    {entry.name}: {entry.start} - {entry.end}
                  </title>
                </rect>

                {/* Split segments (e.g., commuting to another city) */}
                {entry.split?.map((split) => {
                  // Show split as stripes on the bar for the relevant years
                  return split.years.map((splitYear) => {
                    const sx = yearToX(splitYear)
                    const sw = yearToX(splitYear + 1) - sx
                    return (
                      <rect
                        key={`${split.name}-${splitYear}`}
                        x={sx}
                        y={y + barHeight * (1 - split.percent / 100)}
                        width={sw}
                        height={barHeight * (split.percent / 100)}
                        fill={split.color}
                        opacity={0.8}
                      >
                        <title>
                          {split.name} ({split.percent}%)
                        </title>
                      </rect>
                    )
                  })
                })}

                {/* Duration label on bar */}
                {width > 50 && (
                  <text
                    x={x1 + width / 2}
                    y={y + barHeight / 2 + 4}
                    textAnchor="middle"
                    className="timeline-chart__bar-label"
                    fill="#fff"
                  >
                    {entry.end - entry.start} yrs
                  </text>
                )}
              </g>
            )
          })}
        </g>
      </svg>
    </div>
  )
}

export default TimelineChart
